import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { commandesService } from '../services/api';
import { useFormatage } from '../hooks/useAgroshopHooks';

export default function OrderTrackingPage() {
  const { numero } = useParams();
  const navigate = useNavigate();
  const { formatPrix, formatDate } = useFormatage();
  const [orderNumber, setOrderNumber] = useState(numero || '');
  const [commande, setCommande] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const statusSteps = [
    { key: 'confirmee', label: 'Confirmée' },
    { key: 'payee', label: 'Payée' },
    { key: 'preparation', label: 'En préparation' },
    { key: 'expedition', label: 'Expédiée' },
    { key: 'livree', label: 'Livrée' },
  ];

  const loadCommande = async (numeroCommande) => {
    if (!numeroCommande) return;
    setLoading(true);
    setError('');
    setCommande(null);
    try {
      const resultat = await commandesService.getByNumero(numeroCommande.trim());
      setCommande(resultat);
    } catch (err) {
      setError(err.message || 'Commande introuvable. Vérifiez le numéro.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (numero) {
      setOrderNumber(numero);
      loadCommande(numero);
    }
  }, [numero]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!orderNumber.trim()) {
      setError('Veuillez saisir le numéro de commande.');
      return;
    }
    navigate(`/cmd/${encodeURIComponent(orderNumber.trim())}`);
  };

  return (
    <div>
      <section className="ag-card">
        <h2>Suivi de commande</h2>
        <p>Entrez votre numéro de commande pour suivre l’avancement de votre livraison.</p>
        <form className="ag-form" onSubmit={handleSubmit}>
          <label>
            Numéro de commande
            <input
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="Ex: CMD-2024-00145"
            />
          </label>
          <div className="ag-form-actions">
            <button className="ag-btn" type="submit">Voir le suivi</button>
            <button
              type="button"
              className="ag-btn-secondary"
              onClick={() => {
                setOrderNumber('');
                setCommande(null);
                setError('');
              }}
            >
              Réinitialiser
            </button>
          </div>
        </form>
      </section>

      {loading && <div className="ag-alert">Recherche de la commande…</div>}
      {error && <div className="ag-alert ag-alert-error">{error}</div>}

      {commande && (
        <section className="ag-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <h3>Commande {commande.numero}</h3>
              <p>Statut actuel : <strong>{commande.statut_display || commande.statut}</strong></p>
              <p>Client : <strong>{commande.client_nom || 'Anonyme'}</strong></p>
              <p>Téléphone : <strong>{commande.client_telephone || '—'}</strong></p>
            </div>
            <div>
              <p>Montant total : <strong>{formatPrix(commande.montant_total)}</strong></p>
              <p>Livraison : <strong>{commande.mode_retrait === 'retrait' ? 'Retrait en boutique' : commande.mode_retrait === 'point_relais' ? 'Point relais' : 'Livraison à domicile'}</strong></p>
              <p>Région : <strong>{commande.region_livraison || '—'}</strong></p>
              <p>Livraison souhaitée : <strong>{formatDate(commande.date_livraison_souhaitee)}</strong></p>
            </div>
          </div>

          <div className="ag-order-tracker" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '18px' }}>
            {statusSteps.map((step) => {
              const active = statusSteps.findIndex((item) => item.key === commande.statut) >= statusSteps.findIndex((item) => item.key === step.key);
              return (
                <div key={step.key} className={`ag-step ${active ? 'ag-step--active' : ''}`} style={{
                  padding: '10px 14px',
                  borderRadius: '999px',
                  backgroundColor: active ? '#286ef8' : '#f0f3fb',
                  color: active ? '#fff' : '#4a5568',
                  fontSize: '0.9rem',
                }}>
                  {step.label}
                </div>
              );
            })}
          </div>

          <div className="ag-order-summary" style={{ marginTop: '20px' }}>
            <h4>Détails de la commande</h4>
            <div className="ag-card" style={{ padding: '16px' }}>
              <ul className="ag-list-tight">
                {commande.lignes.map((ligne) => (
                  <li key={ligne.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center' }}>
                    <div>
                      <strong>{ligne.produit_nom || ligne.produit_reference}</strong>
                      <div style={{ color: '#9aa19a', fontSize: '0.95rem' }}>{ligne.quantite} × {formatPrix(ligne.prix_unitaire)}</div>
                    </div>
                    <div><strong>{formatPrix(ligne.prix_total)}</strong></div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <button className="ag-btn-secondary" type="button" onClick={() => navigate('/')}>Retour à l’accueil</button>
            <button className="ag-btn" type="button" onClick={() => navigate('/cart')}>Voir mon panier</button>
          </div>
        </section>
      )}
    </div>
  );
}
